import React, { lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';
import { Popup, Menu } from 'semantic-ui-react';
import { Route, Switch, Link, Redirect } from 'react-router-dom';
import styled from 'styled-components';
import * as _ from 'lodash';

import ColoredCircle from 'common/components/Chart/atoms/ColoredCircle';
import colors from 'common/styles/colors';
import RBAC from 'common/rbac/RBAC';
import { mapComponentToResource, operations } from 'common/rbac/constants';
import { checkPermissions, isFmeaEnabled } from 'common/rbac/util';

import lazyRetry from 'common/helpers/componentLoader';
import LoadingSvg from 'common/components/atoms/Loading';
import { FlexContainer } from 'common/components/atoms';
import DynamicTabs from 'common/components/organisms/DynamicTabs';
import AccountMenuItems from 'common/components/organisms/AccountMenuItems';
import MoreOptions from 'common/components/organisms/MoreOptions';
import ActivityTabHeader from 'home/HomePage/components/ActivityTabHeader';
import ErrorBoundary from 'common/components/ErrorBoundary/ErrorBoundary';
import { history } from '../../../common/helpers';
import * as machineDetailsActions from './machineDetails.actions';
import * as analystSummaryActions from './Health/actions/analystSummary.actions';
import { getMachineBuilderMetadata } from '../MachineBuilder/actions/machineBuilder.actions';
import MachineInfoV2 from './MachineInfo/MachineInfoV2';
import BaselinePageV2 from './Health/components/BaselinePageV2';
import DigitalTwinMachineShell from './DigitalTwinExperience/DigitalTwinMachineShell';
import {
  isDigitalTwinShellEnabled,
  shouldShowDigitalTwin3dViewTab,
  DIGITAL_TWIN_3D_TAB_STORAGE_KEY,
} from './DigitalTwinExperience/digitalTwinUiFlags';
import { getWebGLSupportTier } from './Digital3DView';

const MachineHealth = lazy(() => lazyRetry(() => import('home/Machines/MachineDetails/Health')));
const MachineOverview = lazy(() => lazyRetry(() => import('./MachineOverview/MachineOverview')));
const Digital3DView = lazy(() => lazyRetry(() => import('./Digital3DView/Digital3DView')));
const Attachments = lazy(() => lazyRetry(() => import('../../HomePage/organisms/Attachments')));
const Sensors = lazy(() => lazyRetry(() => import('../../Sensors/components/Sensors')));
const MachineActivity = lazy(() => lazyRetry(() => import('./MachineActivity/MachineActivity')));
const MachineCharts = lazy(() => lazyRetry(() => import('./MachineCharts/MachineCharts')));
const MachineInstrumentationAlerts = lazy(() => lazyRetry(() => import('./MachineInstrumentationAlerts/MachineInstrumentationAlerts')));
const MachineFmea = lazy(() => lazyRetry(() => import('./MachineFmea/MachineFmea')));

const BetaSpan = styled.span`
  font-family: 'Petasense Open Sans';
  font-size: 10px;
  font-weight: 600;
  position: relative;
  left: ${props => props.leftMargin ? '-10px' : '0px'};
  top: -10px;
`;

const IndicatorSpan = styled.span`
  position: relative;
  right: ${props => props.rightPos ? props.rightPos : '20px'};
  top: -10px;
`;

class MachineDetails extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeItem: 'overview',
      title: '',
      webGLSupportTier: null,
    };
  }

  getTabItems = () => {
    const prefs = this.props.currentAccount && this.props.currentAccount.preferences;
    const webglTier = this.state.webGLSupportTier;
    const base = [
      'overview',
      'health',
      'baseline',
      'charts',
      'activity',
      'fmea',
      'devices',
      'attachments',
      'info',
      'instrumentation-alerts'
    ];
    return shouldShowDigitalTwin3dViewTab(prefs) && webglTier !== 'none' ? [...base, '3d-view'] : base;
  };

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.updateActiveItem();
    }
    const { machine_id } = this.props.match.params;
    if (machine_id !== prevProps.match.params.machine_id) this.props.machineDetailsActions.getMachineDetails(machine_id);
    if (machine_id !== prevProps.match.params.machine_id) this.props.machineDetailsActions.getMachineCompletionStatus(machine_id);
  }

  componentWillReceiveProps(nextProps) {
    const { title } = this.state;
    const { machineDetails } = nextProps;
    const name = _.get(machineDetails, 'object.name');
    if (name) {
      const newTitle = name.split('_').join(' ');
      if (newTitle !== title) {
        this.setState({ title: newTitle });
      }
    }
  }

  updateActiveItem = () => {
    const { url } = this.props.match;
    const prefs = this.props.currentAccount && this.props.currentAccount.preferences;
    const items = this.props.location.pathname.slice(url.length).split('/');
    const item = items[1];
    if (item === '3d-view' && !shouldShowDigitalTwin3dViewTab(prefs)) {
      history.replace(`${url}/overview`);
      return;
    }
    if (item && item.length > 0) {
      this.setState({ activeItem: item });
    } else {
      history.replace(`${url}/overview`);
    }
  };

  // FMEA visibility needs BOTH the RBAC permission (the `fmea` resource) and the
  // per-account premium gate. isFmeaEnabled lets Petasense staff through always
  // and customers only once the account has the feature enabled (early release).
  hasFmeaAccess = () => (
    isFmeaEnabled(this.props.currentUser, this.props.currentAccount)
    && checkPermissions(
      mapComponentToResource.Fmea,
      this.props.rbacPermissions.result,
      operations.Read,
      this.props.currentUser
    )
  );

  // Keep the visible tab list in sync between the menu, the active-index
  // lookup, and the change handler so the highlighted tab never drifts.
  getVisibleTabItems = () => {
    const tabItems = this.getTabItems();
    if (this.hasFmeaAccess()) return tabItems;
    return tabItems.filter(item => item !== 'fmea');
  };

  handleTabChange = (e, data) => {
    const { activeIndex } = data;
    this.setState({ activeItem: this.getVisibleTabItems()[activeIndex] });
  };

  onChangeTitle = (title) => {
    const { machine_id } = this.props.match.params;
    this.setState({ title: title.machineTitle });
    this.props.machineDetailsActions.updateMachineDetails(machine_id, {
      key: 'name',
      value: title.machineTitle
    });
  };

  isAspectIncomplete = (aspect_name) => {
    const { completionStatus } = this.props;
    if (!completionStatus.loading && completionStatus.data && completionStatus.data.missing_steps) {
      return completionStatus.data.missing_steps.includes(aspect_name);
    }
    return false;
  };

  onDigitalTwin3dTabPrefChanged = () => {
    const { match, location, currentAccount } = this.props;
    const p = currentAccount && currentAccount.preferences;
    const webglTier = getWebGLSupportTier();
    if (
      location.pathname.indexOf('/3d-view') !== -1 &&
      (!shouldShowDigitalTwin3dViewTab(p) || webglTier === 'none')
    ) {
      history.replace(`${match.url}/overview`);
    }
    this.forceUpdate();
  };

  componentDidMount() {
    this.updateActiveItem();
    const { machine_id } = this.props.match.params;
    this.props.machineDetailsActions.getMachineDetails(machine_id);
    this.props.machineDetailsActions.getMachineCompletionStatus(machine_id);
    this.props.getMachineBuilderMetadata();
    this.setState({ webGLSupportTier: getWebGLSupportTier() });
    if (typeof window !== 'undefined') {
      window.addEventListener('digitalTwin3dTabPreferenceChanged', this.onDigitalTwin3dTabPrefChanged);
      window.addEventListener('storage', this.onStorageDigitalTwin3dTab);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('digitalTwin3dTabPreferenceChanged', this.onDigitalTwin3dTabPrefChanged);
      window.removeEventListener('storage', this.onStorageDigitalTwin3dTab);
    }
  }

  onStorageDigitalTwin3dTab = (e) => {
    if (e.key === DIGITAL_TWIN_3D_TAB_STORAGE_KEY && this.onDigitalTwin3dTabPrefChanged) {
      this.onDigitalTwin3dTabPrefChanged();
    }
  };

  machineDetailsPanes = () => {
    const { machine_id } = this.props.match.params;
    const { url } = this.props.match;
    const { activeItem } = this.state;
    const { t } = this.props;
    const canShow3DTab = this.getTabItems().includes('3d-view');
    const menuItems = [
      {
        menuItem: (
          <Menu.Item
            as={Link}
            to={`${url}/overview`}
            key="overview"
            active={activeItem === 'overview'}
          >
            {t('machineDashboard.tabs.overview')}
          </Menu.Item>
        ),
        key: 'machine-menu-overview',
        path: `${url}/overview`,
        title: t('machineDashboard.tabs.overview')
      },
      {
        menuItem: (
          <Popup
            content={t('machineDashboard.baselinesNotSet')}
            disabled={!this.isAspectIncomplete('HEALTH')}
            trigger={(
              <Menu.Item
                as={Link}
                to={`${url}/health`}
                key="health"
                active={activeItem === 'health'}
              >
                {t('machineDashboard.tabs.health')}
                <RBAC
                  resource={mapComponentToResource.Machines}
                  operation={operations.Update}
                  yes={(
                    <FlexContainer>
                      {this.isAspectIncomplete('HEALTH') && (
                        <IndicatorSpan><ColoredCircle color={colors.cyan} /></IndicatorSpan>
                      )}
                      <BetaSpan leftMargin={this.isAspectIncomplete('HEALTH')}>
                        {t('common.beta', { defaultValue: 'beta' })}
                      </BetaSpan>
                    </FlexContainer>
                  )}
                  no={(
                    <BetaSpan>{t('common.beta', { defaultValue: 'beta' })}</BetaSpan>
                  )}
                />
              </Menu.Item>
            )}
          />
        ),
        key: 'machine-menu-health',
        path: `${url}/health`,
        title: t('machineDashboard.tabs.health')
      },
      {
        menuItem: (
          <Popup
            content={t('machineDashboard.baselinesNotSet')}
            disabled={!this.isAspectIncomplete('HEALTH')}
            trigger={(
              <Menu.Item
                className="baseline"
                as={Link}
                to={`${url}/baseline`}
                key="baseline"
                active={activeItem === 'baseline'}
              >
              {t('machineDashboard.tabs.baseline')}
              <RBAC
                resource={mapComponentToResource.Machines}
                operation={operations.Update}
                yes={(
                  <FlexContainer>
                    {this.isAspectIncomplete('HEALTH') && (
                      <IndicatorSpan><ColoredCircle color={colors.cyan} /></IndicatorSpan>
                    )}
                  </FlexContainer>
                )}
              />
              </Menu.Item>
            )}
          />
        ),
        key: 'machine-menu-baseline',
        path: `${url}/baseline`,
        title: t('machineDashboard.tabs.baseline')
      },
      {
        menuItem: (
          <Popup
            content={t('machineDashboard.alarmsNotSet')}
            disabled={!this.isAspectIncomplete('CHARTS')}
            trigger={(
            <Menu.Item
              as={Link}
              to={`${url}/charts`}
              key="charts"
              active={activeItem === 'charts'}
            >
              {t('machineDashboard.tabs.charts')}
              <RBAC
                resource={mapComponentToResource.Machines}
                operation={operations.Update}
                yes={(
                  <FlexContainer>
                    {this.isAspectIncomplete('CHARTS') && (
                    <IndicatorSpan><ColoredCircle color={colors.cyan} /></IndicatorSpan>
                    )}
                  </FlexContainer>
                )}
              />
            </Menu.Item>
            )}
          />
        ),
        key: 'machine-menu-charts',
        path: `${url}/charts`,
        title: t('machineDashboard.tabs.charts')
      },
      {
        menuItem: (
          <Menu.Item
            as={Link}
            to={`${url}/activity`}
            key="activity"
            active={activeItem === 'activity'}
          >
           <ActivityTabHeader machine_id={machine_id} />
          </Menu.Item>
        ),
        key: 'machine-menu-activity',
        path: `${url}/activity`,
        title: t('machineDashboard.tabs.activity')
      },
      {
        menuItem: (
          <Menu.Item
            as={Link}
            to={`${url}/fmea`}
            key="fmea"
            active={activeItem === 'fmea'}
          >
            {t('machineDashboard.tabs.fmea')}
          </Menu.Item>
        ),
        key: 'machine-menu-fmea',
        path: `${url}/fmea`,
        title: t('machineDashboard.tabs.fmea')
      },
      {
        menuItem: (
          <Menu.Item
            as={Link}
            to={`${url}/devices`}
            key="devices"
            active={activeItem === 'devices'}
          >
            {t('machineDashboard.tabs.devices')}
          </Menu.Item>
        ),
        key: 'machine-menu-sensors',
        path: `${url}/devices`,
        title: t('machineDashboard.tabs.devices')
      },
      {
        menuItem: (
          <Menu.Item
            as={Link}
            to={`${url}/attachments`}
            key="attachments"
            active={activeItem === 'attachments'}
          >
            {t('machineDashboard.tabs.attachments')}
          </Menu.Item>
        ),
        key: 'machine-menu-pictures',
        path: `${url}/attachments`,
        title: t('machineDashboard.tabs.attachments')
      },
      {
        menuItem: (
          <Popup
            content={t('machineDashboard.machineInfoIncomplete')}
            disabled={!this.isAspectIncomplete('INFO')}
            trigger={(
              <Menu.Item
                className="info"
                as={Link}
                to={`${url}/info`}
                key="info"
                active={activeItem === 'info'}
              >
              {t('machineDashboard.tabs.info')}
              <RBAC
                resource={mapComponentToResource.Machines}
                operation={operations.Update}
                yes={(
                  <FlexContainer>
                    {this.isAspectIncomplete('INFO') && (
                      <IndicatorSpan rightPos="15px">
                        <ColoredCircle color={colors.cyan} />
                      </IndicatorSpan>
                    )}
                  </FlexContainer>
                )}
              />
              </Menu.Item>
            )}
          />
        ),
        key: 'machine-menu-info',
        path: `${url}/info`,
        title: t('machineDashboard.tabs.info')
      },
      {
        menuItem: (
          <Menu.Item
            as={Link}
            to={`${url}/instrumentation-alerts`}
            key="instrumentation-alerts"
            active={activeItem === 'instrumentation-alerts'}
          >
           {t('machineDashboard.tabs.instrumentationAlerts')}
          </Menu.Item>
        ),
        key: 'machine-menu-instrumentation-alerts',
        path: `${url}/instrumentation-alerts`,
        title: t('machineDashboard.tabs.instrumentationAlerts')
      },
    ];
    if (canShow3DTab) {
      menuItems.push({
        menuItem: (
          <RBAC
            resource={mapComponentToResource.Machines}
            operation={operations.Read}
            yes={(
              <Menu.Item
                as={Link}
                to={`${url}/3d-view`}
                key="3d-view"
                active={activeItem === '3d-view'}
              >
                {t('machineDashboard.tabs.threeDView')}
                <BetaSpan>BETA</BetaSpan>
              </Menu.Item>
            )}
            no={null}
          />
        ),
        key: 'machine-menu-3d-view',
        path: `${url}/3d-view`,
        title: t('machineDashboard.tabs.threeDView')
      });
    }
    if (!this.hasFmeaAccess()) {
      return menuItems.filter(item => item.key !== 'machine-menu-fmea');
    }
    return menuItems;
  };

  renderMachineDetails = () => {
    const { machineDetails } = this.props;
    const { activeItem } = this.state;
    const tabItems = this.getTabItems();
    const tabIndex = tabItems.indexOf(activeItem);
    const prefs = this.props.currentAccount && this.props.currentAccount.preferences;
    const digitalTwinUi = isDigitalTwinShellEnabled();
    const webglTier = this.state.webGLSupportTier;
    // Must match getTabItems(): tab is shown when prefs/toggle allow 3D tab and WebGL works — not only when the new Digital Twin shell is on.
    const show3dViewRoute = shouldShowDigitalTwin3dViewTab(prefs) && webglTier !== 'none';
    if (!machineDetails || machineDetails.loading) {
      return (
        <div className="full-screen-center">
          <LoadingSvg />
        </div>
      );
    }
    if (machineDetails.error) {
      return <div>{this.props.t('common.errors.errorLoadingMachineDetails', { defaultValue: 'Error loading machine details' })}</div>;
    }
    if (machineDetails.object) {
      const { machine_id } = this.props.match.params;
      const tabs = (
        <DynamicTabs
          menu={{ secondary: true }}
          panes={this.machineDetailsPanes()}
          activeIndex={tabIndex >= 0 ? tabIndex : 0}
          onTabChange={this.handleTabChange}
          bufferSpace={45}
        />
      );
      const routes = (
        <Suspense fallback={(
            <LoadingSvg height="100%" />
          )}
        >
          <ErrorBoundary name="machine-details-tab" variant="page" resetKeys={[machine_id, activeItem]}>
          <Switch>
            <Route
              path="/machines/:machine_id/overview"
              component={MachineOverview}
            />
            <Route
              path="/machines/:machine_id/health"
              component={MachineHealth}
            />
            <Route
              path="/machines/:machine_id/baseline"
              render={() => (
                <div>
                  <BaselinePageV2 machineId={this.props.match.params.machine_id} />
                </div>
              )}
            />
            <Route
              path="/machines/:machine_id/charts"
              component={MachineCharts}
            />
            <Route
              path="/machines/:machine_id/activity"
              component={MachineActivity}
            />
            <Route
              path="/machines/:machine_id/fmea"
              render={props => (
                this.hasFmeaAccess()
                  ? <MachineFmea {...props} />
                  : <Redirect to={`/machines/${this.props.match.params.machine_id}/overview`} />
              )}
            />
            <Route path="/machines/:machine_id/devices" component={Sensors} />
            <Route
              path="/machines/:machine_id/attachments"
              render={() => (
                <>
                  {/* Attachments is shared with the task/note modals, so the menu goes here. */}
                  <MoreOptions>
                    <AccountMenuItems />
                  </MoreOptions>
                  <Attachments
                    originId={this.props.match.params.machine_id}
                    originType="machine"
                    allowUpload
                    showExistingAtts
                    showSelectedFiles={false}
                    uploadButtonText={this.props.t('machineDashboard.attachments.uploadFilesButton')}
                  />
                </>
              )}
            />
            <Route path="/machines/:machine_id/info" component={MachineInfoV2} />
            <Route
              path="/machines/:machine_id/instrumentation-alerts"
              component={MachineInstrumentationAlerts}
            />
            {show3dViewRoute && (
              <Route
                path="/machines/:machine_id/3d-view"
                component={Digital3DView}
              />
            )}
          </Switch>
          </ErrorBoundary>
        </Suspense>
      );

      const inner = digitalTwinUi ? (
        <DigitalTwinMachineShell
          machineId={machine_id}
          noContentPadding={activeItem === '3d-view'}
        >
          {tabs}
          {routes}
        </DigitalTwinMachineShell>
      ) : (
        <div>
          {tabs}
          <div style={activeItem === '3d-view' ? { position: 'relative', height: 'calc(100vh - var(--title-bar-offset, 0px) - 160px)', overflow: 'hidden' } : undefined}>
            {routes}
          </div>
        </div>
      );

      return (
        <div className="machine-details">
          <div>
            {inner}
          </div>
        </div>
      );
    }
    return <div />;
  };

  render() {
    return <div>{this.renderMachineDetails()}</div>;
  }
}

const mapStateToProps = state => ({
  machineDetails: state.machineDetails.details,
  currentUser: state.user.user,
  currentAccount: state.currentAccount,
  rbacPermissions: state.rbacPermissions,
  analystSummary: state.machineDetails.AS.summary,
  completionStatus: state.machineDetails.completion_status
});

const mapDispatchToProps = dispatch => ({
  getMachineBuilderMetadata: bindActionCreators(getMachineBuilderMetadata, dispatch),
  machineDetailsActions: bindActionCreators(machineDetailsActions, dispatch),
  analystSummaryActions: bindActionCreators(analystSummaryActions, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(MachineDetails));
